// The web app / PWA shell (D-133). Second shell of the shared core: it mounts
// the exact same page modules as the extension, but with a hash router instead
// of extension pages/tabs, and platform=web (IndexedDB, Web Notifications). No
// chrome.* — the platform seam handles all host differences.
//
// NOTE: not yet a finished product. Live market data needs the control-plane
// DATA PROXY (a web origin has no host_permissions, so provider fetches are
// CORS-blocked); until that lands the shell renders but prices stay empty.
import { el, initTooltips } from "../lib/dom.js";
import { applyStoredTheme } from "../lib/theme.js";
import { platform } from "../lib/platform/index.js";
import { renderWatchlistPage } from "../pages/watchlist.js";
import { renderDeployPage } from "../pages/deploy.js";
import { renderTodayPage } from "../pages/today.js";
import { renderJournalPage } from "../pages/journal.js";
import { renderMorePage } from "../pages/more.js";
import { renderConnections } from "../lib/connections-view.js";
import { showSetupSettings } from "../lib/setup-settings-overlay.js";
import { confluenceStore } from "../lib/confluence-store.js";
import { licenseGateEnabled, refreshLicenseStatus, licenseState, onLicenseChange, signInWithMagicToken } from "../lib/license.js";
import { renderLicenseGate } from "../lib/license-gate.js";
import { reconcileFiredAlerts, reconcileSetupFires } from "../lib/web-push.js";
import { startCloudSync } from "../lib/cloud-sync.js";
import { mountInstallPrompt, mountExtensionSuggestion } from "../lib/install-prompt.js";
import { canLeaveForNav } from "../lib/mode-guard.js";
import { modeSatisfied, onDataModeChange, getDataMode } from "../lib/data-mode.js";
import { storageSet, onStorageChange } from "../lib/storage.js";
import type { Card } from "../lib/confluence.js";

type NavItem = { id: string; label: string; render: (root: HTMLElement) => (() => void) | Promise<() => void> };
const NAV: NavItem[] = [
  { id: "watch", label: "Build", render: renderWatchlistPage },
  { id: "deploy", label: "Deploy", render: renderDeployPage },
  { id: "monitor", label: "Monitor", render: renderTodayPage },
  { id: "journal", label: "Journal", render: renderJournalPage },
  { id: "setup", label: "Setup", render: renderMorePage },
];

let teardown: () => void = () => {};
let navBar: HTMLElement;

function setActive(id: string): void {
  for (const btn of Array.from(navBar.children) as HTMLElement[]) {
    btn.style.color = btn.dataset["id"] === id ? "var(--text-primary)" : "var(--text-secondary)";
    btn.style.fontWeight = btn.dataset["id"] === id ? "600" : "400";
  }
}

// Nav lock (D-152): while the mode is unsatisfied (Premium with no connection
// established), every tab EXCEPT Setup is greyed out and unclickable, and the
// app is parked on Setup — which hosts the Free/Premium toggle and the
// connection checklist — so the only ways forward are to connect a feed or flip
// back to Free. Tabs unlock the moment a connection exists (or mode is Free).
async function refreshNavLock(): Promise<void> {
  if (!navBar) return;
  const ok = await modeSatisfied();
  const free = (await getDataMode()) === "free";
  for (const btn of Array.from(navBar.children) as HTMLButtonElement[]) {
    const id = btn.dataset["id"];
    // Premium-with-no-connection locks everything but Setup. Deploy is ALSO locked
    // in Free mode — deploying a live strategy needs a real data/broker connection.
    const deployLocked = free && id === "deploy";
    const locked = (!ok && id !== "setup") || deployLocked;
    btn.disabled = locked;
    btn.style.opacity = locked ? "0.38" : "";
    btn.style.cursor = locked ? "default" : "pointer";
    btn.setAttribute("aria-disabled", locked ? "true" : "false");
    btn.title = deployLocked ? "Deploying a live strategy needs a data/broker connection — switch to Premium in Setup"
      : locked ? "Connect a data feed or switch to Free (Setup)" : "";
  }
  // Park on the connection tab (Setup) when unsatisfied — but don't yank the
  // user out of the Connections surface they may be actively setting up.
  if (!ok && location.hash !== "#/setup" && location.hash !== "#/connections") {
    location.hash = "#/setup";
  }
  // Free mode can't sit on Deploy — bounce to Build if that's where we are.
  if (free && location.hash === "#/deploy") location.hash = "#/watch";
}

async function mount(): Promise<void> {
  teardown();
  teardown = () => {};
  const view = document.getElementById("view")!;
  view.replaceChildren();
  const hash = location.hash || "#/watch";

  // Builder route — the Setups/Build/Confirm surface for one symbol.
  if (hash.startsWith("#/build/")) {
    navBar.style.display = "none";
    const sym = decodeURIComponent(hash.slice("#/build/".length)).toUpperCase();
    await confluenceStore.init();
    const setups = confluenceStore.setupsForSym(sym).map((c) => ({ card: c, live: confluenceStore.isLive(c.id) }));
    showSetupSettings({ symbol: sym, setups, at: Date.now() }, {
      beforeOpen: () => document.getElementById("setup-settings-overlay")?.remove(),
      onClose: () => { location.hash = "#/watch"; },
    });
    return;
  }
  // Connections route — the full Connections surface.
  if (hash === "#/connections") {
    navBar.style.display = "none";
    teardown = await renderConnections(view, () => { location.hash = "#/setup"; });
    return;
  }

  navBar.style.display = "flex";
  const item = NAV.find((n) => hash === `#/${n.id}`) ?? NAV[0]!;
  setActive(item.id);
  teardown = (await item.render(view)) ?? (() => {});
  void refreshNavLock();
}

async function main(): Promise<void> {
  await applyStoredTheme();
  initTooltips();

  // Magic-link landing (D-151): if we arrived from an emailed sign-in link
  // (…/?login=<token>), exchange it for a session, then strip the token from the
  // URL so it isn't left in history or re-used. Success flows straight into the
  // app below (the license is now stored); failure falls through to the gate.
  const magicToken = new URLSearchParams(location.search).get("login");
  if (magicToken) {
    await signInWithMagicToken(magicToken);
    const clean = new URL(location.href);
    clean.searchParams.delete("login");
    history.replaceState(null, "", clean.toString());
  }

  // License gate (D-017; ENABLED at launch — GATE_DEFAULT_ENABLED=true, D-140).
  // Same flow as the extension popup, reusing the chrome-free gate. The lapse
  // screen keeps journal + export reachable (the rider), so we return before
  // mounting the app only while the gate is enabled AND the license isn't active.
  // `?gate=1` forces the sign-in screen for pre-launch testing (the flag ships off).
  const forceGate = new URLSearchParams(location.search).has("gate");
  if (forceGate || (await licenseGateEnabled())) {
    await refreshLicenseStatus();
    const state = await licenseState();
    if (state !== "active") {
      const gateRoot = el("div", { style: { height: "100vh" } });
      document.body.replaceChildren(gateRoot);
      renderLicenseGate(gateRoot, state, () => location.reload());
      mountInstallPrompt(); // installable even before sign-in, so the "app" is on the home screen
      onLicenseChange((next) => {
        if (next === "active") location.reload();
      });
      return;
    }
  }

  navBar = el("nav", { style: { display: "flex", gap: "18px", padding: "10px 16px", borderBottom: "1px solid var(--border)", flex: "none" } });
  for (const n of NAV) {
    const btn = el("button", { text: n.label, style: { background: "none", border: "0", cursor: "pointer", font: "var(--font-header)", color: "var(--text-secondary)" } });
    btn.dataset["id"] = n.id;
    btn.addEventListener("click", () => {
      const target = `#/${n.id}`;
      if (location.hash === target || (location.hash === "" && n.id === "watch")) return;
      // D-152 guard: Premium with no connection can't leave the current tab.
      void canLeaveForNav(() => { location.hash = "#/connections"; }).then((ok) => { if (ok) location.hash = target; });
    });
    navBar.appendChild(btn);
  }
  const view = el("div", { attrs: { id: "view" }, style: { flex: "1", minHeight: "0", overflowY: "auto" } });
  document.body.append(navBar, view);

  window.addEventListener("hashchange", () => void mount());
  // Re-evaluate the nav lock whenever the mode flips (Free⇄Premium): flipping to
  // Premium with no connection greys the other tabs and parks on Setup.
  onDataModeChange(() => void refreshNavLock());

  // The builder overlay streams library actions (save / live / duplicate / delete)
  // via the "setupLibraryAction" storage key. In the extension the watchlist page
  // applies these; in the web shell that page isn't mounted while the builder route
  // is open, so we apply them globally here, then re-push the setups so the open
  // overlay refreshes (and re-render the builder route to show the change).
  await confluenceStore.init();
  onStorageChange<{ symbol?: string; op?: string; id?: string; on?: boolean; card?: unknown } | null>("setupLibraryAction", (v) => {
    if (!v || !v.symbol || !v.op) return;
    const sym = v.symbol;
    void (async () => {
      if (v.op === "live" && v.id) await confluenceStore.setLive(v.id, !!v.on);
      else if (v.op === "duplicate" && v.id) await confluenceStore.duplicateSetup(v.id);
      else if (v.op === "delete" && v.id) await confluenceStore.deleteSetup(v.id);
      else if (v.op === "save" && v.card) await confluenceStore.upsertSetup(v.card as Card);
      else if (v.op === "applyAll" && v.card) await confluenceStore.applySetupToAll(v.card as Card); // D-158 watchlist-wide scan
      const setups = confluenceStore.setupsForSym(sym).map((card) => ({ card, live: confluenceStore.isLive(card.id) }));
      await storageSet("setupSettings", { symbol: sym, setups, at: Date.now() });
    })();
  });
  // Refreshed setups → re-render the builder route so the Setups list reflects the save.
  onStorageChange<unknown>("setupSettings", () => {
    if (location.hash.startsWith("#/build/")) void mount();
  });
  // In-app navigate events (e.g. a notification deep-link) → route by page id.
  window.addEventListener("tickahead:navigate", (e) => {
    const page = (e as CustomEvent).detail;
    if (typeof page === "number" && NAV[page]) location.hash = `#/${NAV[page]!.id}`;
  });

  if (platform.kind !== "web") console.warn("web shell running with non-web platform");
  await mount();

  // Offer "add to home screen" (mobile PWA) or "get the extension" (desktop) so
  // the app installs like a normal app / the desktop power-up gets discovered (D-145).
  mountInstallPrompt();
  mountExtensionSuggestion();

  // Register the service worker (installability + offline shell + future push).
  if ("serviceWorker" in navigator) {
    try { await navigator.serviceWorker.register("./sw.js"); } catch { /* dev / unsupported */ }
  }

  // Reconcile anything the server fired while the app was closed — price alerts
  // (D-137) and confluence setups (D-138) — into the in-app feed.
  void reconcileFiredAlerts();
  void reconcileSetupFires();

  // Account sync (D-139): pull the user's encrypted data down, seed local-only
  // data up, then keep it in sync across devices. Signed-in users only.
  void startCloudSync();
}

void main();
